import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Trash2, GripVertical, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const INPUT_TYPES = [
  { value: "bool", label: "bool" },
  { value: "date", label: "date" },
  { value: "number", label: "number" },
  { value: "text", label: "text" },
];

function uid() {
  return `s_${Math.random().toString(36).slice(2, 10)}`;
}

export default function FormTemplateModal({ mode, template, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || "");
  const [requiresVerify, setRequiresVerify] = useState(!!template?.requires_supervisor_verification);
  const [steps, setSteps] = useState(
    (template?.steps || []).map((s) => ({ ...s })).length > 0
      ? template.steps.map((s) => ({ ...s }))
      : [{ id: uid(), label: "", input_type: "bool", required: false }],
  );
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const updateStep = (idx, patch) =>
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const addStep = () =>
    setSteps((prev) => [...prev, { id: uid(), label: "", input_type: "bool", required: false }]);
  const removeStep = (idx) =>
    setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  const moveStep = (idx, dir) => {
    setSteps((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Please give the form a name."); return; }
    const cleaned = steps
      .map((s) => ({ ...s, label: (s.label || "").trim() }))
      .filter((s) => s.label.length > 0);
    if (cleaned.length === 0) { toast.error("Add at least one step."); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        category: category.trim(),
        steps: cleaned,
        requires_supervisor_verification: !!requiresVerify,
      };
      if (isEdit) {
        await api.put(`/forms/templates/${template.id}`, body);
        toast.success("Form updated");
      } else {
        await api.post("/forms/templates", body);
        toast.success("Form created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="form-template-modal"
    >
      <div
        className="vb-card p-0 w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">
              {isEdit ? "Edit form" : "Create new form"}
            </h2>
            <p className="text-xs opacity-70 mt-0.5">
              Define the steps your team fills in every time.
            </p>
          </div>
          <button className="vb-btn ghost" onClick={onClose} data-testid="form-template-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs opacity-70 mb-1">Name of form *</div>
              <input
                className="vb-input w-full"
                placeholder="e.g. Opening Kitchen Checklist"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="form-template-name"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-70 mb-1">Category</div>
              <input
                className="vb-input w-full"
                placeholder="e.g. Cleaning, Opening, Delivery"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="form-template-category"
              />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Steps</div>
              <button className="vb-btn ghost" onClick={addStep} data-testid="form-template-add-step">
                <Plus size={14} /> Add step
              </button>
            </div>
            <div className="space-y-2" data-testid="form-template-steps">
              {steps.map((s, idx) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2 border rounded-md p-2 bg-white"
                  data-testid={`form-template-step-${idx}`}
                >
                  <button
                    className="text-gray-400 hover:text-gray-600 cursor-grab"
                    onClick={() => moveStep(idx, -1)}
                    title="Move up"
                    data-testid={`form-template-move-up-${idx}`}
                  >
                    <GripVertical size={14} />
                  </button>
                  <input
                    className="vb-input flex-1 min-w-0"
                    placeholder="Enter step details (e.g. Wipe blades)"
                    value={s.label}
                    onChange={(e) => updateStep(idx, { label: e.target.value })}
                    data-testid={`form-template-step-label-${idx}`}
                  />
                  <select
                    className="vb-input"
                    style={{ width: 110 }}
                    value={s.input_type}
                    onChange={(e) => updateStep(idx, { input_type: e.target.value })}
                    data-testid={`form-template-step-type-${idx}`}
                  >
                    {INPUT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-sm select-none">
                    <input
                      type="checkbox"
                      checked={!!s.required}
                      onChange={(e) => updateStep(idx, { required: e.target.checked })}
                      data-testid={`form-template-step-required-${idx}`}
                    />
                    Required
                  </label>
                  <button
                    className="text-red-500 hover:text-red-700 p-1"
                    onClick={() => removeStep(idx)}
                    title="Delete step"
                    disabled={steps.length <= 1}
                    data-testid={`form-template-step-delete-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border rounded-md p-3 bg-black/[0.02]">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={requiresVerify}
                onChange={(e) => setRequiresVerify(e.target.checked)}
                data-testid="form-template-requires-verify"
              />
              <span className="inline-flex items-center gap-1 text-sm font-medium">
                <ShieldCheck size={14} /> Requires supervisor verification
              </span>
            </label>
            <p className="text-xs opacity-70 mt-1 pl-6">
              When on, a supervisor (or higher) must review each submission and click <b>Verify</b> before it counts as complete for audit reports.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <button className="vb-btn ghost" onClick={onClose} data-testid="form-template-cancel">Cancel</button>
          <button
            className="vb-btn primary"
            onClick={save}
            disabled={saving}
            data-testid="form-template-save"
          >
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create form")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
